import { useEffect, useRef, useState } from "react";
import { Clock, PenTool, Send, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCreateComment } from "@/hooks/use-watch";
import { useAnnotation } from "./annotation-context";
import { AnnotationToolbar } from "./annotation-toolbar";
import { VisibilityDropdown, type CommentVisibility } from "./visibility-dropdown";

type Props = {
  mode: "workspace" | "guest" | "share";
  /** workspace = current videoId, guest = share token. */
  watchKey: string | undefined;
  /** Version id the viewer is currently on — becomes the comment's videoId. */
  currentVersionId: string | null;
  /** Live reader for the video's currentTime (imperative, no re-render churn). */
  getCurrentTime: () => number;
  /** Asset type of the version. Image hides the timecode chip + timestamp
   *  toggle and always sends `timestampSeconds: null` (backend rejects a
   *  numeric timestamp on image comments per project-versions-model §"Comment
   *  anchor rules"). Video/audio behavior unchanged. */
  assetType?: "video" | "audio" | "image";
};

/**
 * Comment composer (Task 11).
 *
 * Layout order per spec (locked screenshot):
 *   [yellow timecode chip] [text input] [timestamp toggle] [annotation] [send]
 *
 * State semantics:
 *   - timestampOn (default true): when true, submitting attaches the current
 *     playhead as timestampSeconds. When false, timestampSeconds = null and
 *     the comment is a general note (no timeline dot).
 *   - annotation button: opens the drawing toolbar (Task 12). Task 11 renders
 *     the button as a placeholder — the toolbar isn't built yet.
 *
 * Cut per spec: emoji picker, Public/Private visibility dropdown (all modes).
 */
export function CommentComposer({ mode, watchKey, currentVersionId, getCurrentTime, assetType }: Props) {
  const isImage = assetType === "image";
  const [draft, setDraft] = useState("");
  const [timestampOn, setTimestampOn] = useState(true);
  const [displayTime, setDisplayTime] = useState(0);
  const [visibility, setVisibility] = useState<CommentVisibility>("public");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const create = useCreateComment(mode, watchKey);
  const annotation = useAnnotation();

  // Poll the video's currentTime while the composer is mounted, so the chip
  // stays fresh as playback progresses. 4Hz is plenty for a chip; the video's
  // own timeupdate rate is higher but only the player needs precision. Skip
  // entirely for images — no clock to read, saves the setInterval churn.
  useEffect(() => {
    if (isImage) return;
    const id = window.setInterval(() => setDisplayTime(getCurrentTime()), 250);
    setDisplayTime(getCurrentTime());
    return () => window.clearInterval(id);
  }, [getCurrentTime, isImage]);

  function submit() {
    const content = draft.trim();
    if (!content || !currentVersionId) return;
    const now = getCurrentTime();
    // Ship any shapes present on the draft — the toolbar being closed
    // doesn't mean the user dropped the annotation. Clear happens on post
    // success or on the Clear button below.
    const shapes = annotation.shapes;
    create.mutate(
      {
        currentVersionId,
        content,
        // Backend rejects numeric timestampSeconds on image comments.
        // Force-null when image, ignoring the timestampOn toggle (which is
        // hidden from the UI anyway).
        timestampSeconds: isImage ? null : timestampOn ? now : null,
        annotations: shapes.length > 0 ? { shapes } : null,
        visibility,
      },
      {
        onSuccess: () => {
          setDraft("");
          // Drop the draft annotations + exit annotation mode after posting.
          annotation.clear();
          annotation.deactivate();
          textareaRef.current?.focus();
        },
      },
    );
  }

  const canSend = draft.trim().length > 0 && !create.isPending && !!currentVersionId;

  return (
    <div className="shrink-0 border-t border-border bg-background p-3">
      {/* Upper row — the textarea gets the whole width, taller by default so the
          comment area feels like a proper input, not a cramped one-liner. */}
      <textarea
        ref={textareaRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Leave your comment..."
        rows={3}
        className="min-h-[72px] w-full resize-none rounded-md border border-border bg-background p-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
      />

      {/* Lower row — timestamp toggle, annotation trigger, timecode chip,
          visibility dropdown, send. All on one line under the textarea.
          Annotation mode swaps this row for the AnnotationToolbar. */}
      {annotation.isActive ? (
        <div className="mt-2">
          <AnnotationToolbar />
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-1.5">
          {/* Timestamp toggle only makes sense with a time axis — hidden for
              image comments (they carry spatial anchor + no timestamp). */}
          {!isImage && (
            <ToolButton
              active={timestampOn}
              label={timestampOn ? "Timeline attached" : "Timeline detached"}
              onClick={() => setTimestampOn((v) => !v)}
            >
              <Clock className="h-3.5 w-3.5" />
            </ToolButton>
          )}
          <ToolButton
            active={annotation.isActive || annotation.shapes.length > 0}
            label={
              annotation.shapes.length > 0
                ? `Annotation attached (${annotation.shapes.length} shape${annotation.shapes.length === 1 ? "" : "s"}) — click to edit`
                : "Draw annotation"
            }
            onClick={() => annotation.activate()}
          >
            <PenTool className="h-3.5 w-3.5" />
          </ToolButton>

          {/* Annotation-attached chip — shown ONLY when the toolbar is closed
              but shapes exist. Click the number to reopen the toolbar; click
              the × to drop the drawing. Same shape-count feedback shown in
              the Draw button's tooltip, mirrored here as a visible affordance
              so the state isn't invisible. */}
          {annotation.shapes.length > 0 && (
            <div className="flex items-center gap-1 rounded-md bg-primary/10 pl-1.5 text-[11px] font-medium text-primary">
              <button
                type="button"
                onClick={() => annotation.activate()}
                title="Edit annotation"
                className="hover:underline"
              >
                {annotation.shapes.length} shape{annotation.shapes.length === 1 ? "" : "s"}
              </button>
              <button
                type="button"
                onClick={() => annotation.clear()}
                title="Clear annotation"
                aria-label="Clear annotation"
                className="grid h-5 w-5 place-items-center rounded text-primary/70 transition-colors hover:bg-primary/20 hover:text-primary"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Yellow timecode chip — click to toggle timestamp on/off. Hidden
              for image comments since there's no time axis to attach to. */}
          {!isImage && (
            <button
              type="button"
              onClick={() => setTimestampOn((v) => !v)}
              title={timestampOn ? "Attached to timeline (click to detach)" : "General note (click to attach timeline)"}
              className={cn(
                "shrink-0 rounded-md px-1.5 py-1 font-mono text-[11px] font-semibold transition-colors",
                timestampOn
                  ? "bg-yellow-400/20 text-yellow-500 hover:bg-yellow-400/30"
                  : "bg-secondary text-muted-foreground hover:bg-secondary/70 line-through",
              )}
            >
              {formatTimecode(displayTime)}
            </button>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            <VisibilityDropdown value={visibility} onChange={setVisibility} />
            <button
              type="button"
              onClick={submit}
              disabled={!canSend}
              aria-label="Send comment"
              title="Send (Ctrl/Cmd + Enter)"
              className={cn(
                "grid h-8 w-8 shrink-0 place-items-center rounded-md transition-colors",
                canSend
                  ? "bg-primary text-primary-foreground hover:bg-primary/90"
                  : "bg-secondary text-muted-foreground",
              )}
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {create.error && (
        <div className="mt-1 truncate text-xs text-destructive">
          {(create.error as Error).message || "Failed to send"}
        </div>
      )}
    </div>
  );
}

function ToolButton({
  children,
  onClick,
  label,
  disabled,
  active,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  label: string;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        "grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors",
        !disabled && "hover:bg-secondary hover:text-foreground",
        disabled && "cursor-not-allowed opacity-40",
        active && "bg-primary/10 text-primary hover:bg-primary/15",
      )}
    >
      {children}
    </button>
  );
}

function formatTimecode(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0;
  const totalMs = Math.floor(seconds * 1000);
  const hh = Math.floor(totalMs / 3_600_000);
  const mm = Math.floor((totalMs % 3_600_000) / 60_000);
  const ss = Math.floor((totalMs % 60_000) / 1000);
  const ms = totalMs % 1000;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}.${padMs(ms)}`;
}
const pad = (n: number) => n.toString().padStart(2, "0");
const padMs = (n: number) => n.toString().padStart(3, "0");
